// A vertical sidebar that resizes by dragging an edge. Width
// persists to localStorage, and the resize handle is keyboard-
// accessible (focus → arrows nudge, Home/End jump to the bounds).
//
// One component for both sides — the only difference is which edge
// the handle lives on and which way pointer-x deltas widen the rail.
// Hidden below `md` so the SourcePane gets the full width on phones.

import { useCallback, useRef, useState } from 'react';

const KEYBOARD_STEP = 16;

type Side = 'left' | 'right';

export type ResizableSideRailProps = {
  /** Which edge of the viewport this rail lives on. The drag handle
   *  is rendered on the *opposite* edge of the rail (a left rail's
   *  handle is on its right edge; a right rail's handle is on its
   *  left edge). */
  side: Side;
  /** Stable localStorage key for the width. */
  storageKey: string;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
  /** Accessible label on the resize separator. */
  ariaLabel: string;
  /** Behavior at < md.
   *   - 'hide'         (default): rail wrapper is `display: none` so
   *                    nothing renders. Pick this when the rail's
   *                    contents have no mobile UI of their own.
   *   - 'pass-through': rail wrapper is `display: contents` so its
   *                    children render directly under the parent
   *                    flex layout. Pick this when the rail's
   *                    contents include a `position: fixed` mobile
   *                    drawer that needs to escape the wrapper. */
  mobileBehavior?: 'hide' | 'pass-through';
  /** When true, the rail forces its width to `collapsedWidth` and
   *  disables the resize handle. The user's last expanded width is
   *  preserved in localStorage; toggling collapsed back to false
   *  restores it. */
  collapsed?: boolean;
  /** Width applied while `collapsed` is true. Defaults to 44px. */
  collapsedWidth?: number;
  children: React.ReactNode;
};

export function ResizableSideRail({
  side,
  storageKey,
  defaultWidth,
  minWidth,
  maxWidth,
  ariaLabel,
  mobileBehavior = 'hide',
  collapsed = false,
  collapsedWidth = 44,
  children,
}: ResizableSideRailProps) {
  const [width, setWidth] = useState<number>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return defaultWidth;
      const n = Number(raw);
      if (!Number.isFinite(n)) return defaultWidth;
      return Math.min(maxWidth, Math.max(minWidth, n));
    } catch {
      return defaultWidth;
    }
  });

  const dragStartRef = useRef<{ x: number; w: number } | null>(null);

  const persistWidth = useCallback(
    (w: number) => {
      try {
        localStorage.setItem(storageKey, String(w));
      } catch {
        // Private mode / quota — width still works in-memory.
      }
    },
    [storageKey],
  );

  const clamp = useCallback(
    (n: number) => Math.min(maxWidth, Math.max(minWidth, n)),
    [minWidth, maxWidth],
  );

  // Sign convention: dragging the handle outward grows the rail.
  // For a right rail (handle on its left edge) that's `start.x -
  // e.clientX`; for a left rail (handle on its right edge) it's
  // `e.clientX - start.x`.
  const widenSign = side === 'left' ? 1 : -1;

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStartRef.current = { x: e.clientX, w: width };
  }, [width]);

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const start = dragStartRef.current;
      if (!start) return;
      setWidth(clamp(start.w + widenSign * (e.clientX - start.x)));
    },
    [clamp, widenSign],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragStartRef.current) return;
      dragStartRef.current = null;
      e.currentTarget.releasePointerCapture(e.pointerId);
      persistWidth(width);
    },
    [persistWidth, width],
  );

  // Keyboard convention follows visual direction: ArrowLeft makes a
  // *right* rail wider (it grows leftward) and a *left* rail narrower.
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      let next: number | null = null;
      if (e.key === 'ArrowLeft') {
        next = clamp(width + (side === 'right' ? KEYBOARD_STEP : -KEYBOARD_STEP));
      } else if (e.key === 'ArrowRight') {
        next = clamp(width + (side === 'right' ? -KEYBOARD_STEP : KEYBOARD_STEP));
      } else if (e.key === 'Home') {
        next = maxWidth;
      } else if (e.key === 'End') {
        next = minWidth;
      }
      if (next === null) return;
      e.preventDefault();
      setWidth(next);
      persistWidth(next);
    },
    [width, clamp, minWidth, maxWidth, persistWidth, side],
  );

  // Mobile transparency: at < md the wrapper uses `display: contents`
  // so its children (the rail content) pass through to the parent
  // flex layout. This matters for the left rail because LeftRack
  // renders its own mobile off-canvas drawer with `position: fixed`,
  // and `display: contents` on the wrapper means the wrapper doesn't
  // generate a box that would clip / hide the fixed descendant. The
  // resize handle stays hidden on mobile and while collapsed.
  const handle = collapsed ? null : (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      aria-valuemin={minWidth}
      aria-valuemax={maxWidth}
      aria-valuenow={width}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onKeyDown={onKeyDown}
      className="hidden md:block w-1 shrink-0 bg-zinc-900 hover:bg-yellow-300/40 focus-visible:bg-yellow-300/60 focus-visible:outline-none active:bg-yellow-300/60 cursor-col-resize transition-colors"
    />
  );

  // Width applied when `display: flex` engages (md+). Collapsed
  // overrides the user's stored width; the stored value is preserved
  // for when the rail re-expands.
  const effectiveWidth = collapsed ? collapsedWidth : width;

  // Outer + child display class depends on mobileBehavior. 'hide'
  // collapses the whole subtree on mobile (matching the previous
  // right-rail pattern). 'pass-through' renders the children inline
  // so any fixed-positioned mobile drawer inside them still works.
  const outerCls =
    mobileBehavior === 'hide'
      ? 'hidden md:flex shrink-0 min-h-0 relative'
      : 'contents md:flex md:shrink-0 md:min-h-0 md:relative';
  const innerCls =
    mobileBehavior === 'hide'
      ? 'flex-1 min-w-0 min-h-0'
      : 'contents md:flex-1 md:min-w-0 md:min-h-0 md:block';

  return (
    <div className={outerCls} style={{ width: effectiveWidth }}>
      {side === 'left' ? (
        <>
          <div className={innerCls}>{children}</div>
          {handle}
        </>
      ) : (
        <>
          {handle}
          <div className={innerCls}>{children}</div>
        </>
      )}
    </div>
  );
}
